import { useEffect, useState } from "react";
import { Box, Typography, Paper, CircularProgress, Button, Stack } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import HomeIcon from "@mui/icons-material/Home";
import { useParams, useNavigate, useLocation } from "react-router-dom";

export default function RejectPage() {
  const { application_id } = useParams<{ application_id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<
    "loading" | "success" | "fail" | "alreadyRejected" | "cannotReject"
  >("loading");

  useEffect(() => {
    if (!application_id) return setStatus("fail");

    const params = new URLSearchParams(location.search);
    const device_id = params.get('device_id');

    // device_id가 있으면 해당 기기만 반송 처리
    if (device_id) {
      fetch(`/api/applications/${application_id}/devices/${device_id}`, {
        method: "PATCH",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: '반송' })
      })
        .then(async (res) => {
          if (res.ok) return setStatus("success");
          if (res.status === 409) {
            setStatus("alreadyRejected");
          } else {
            setStatus("fail");
          }
        })
        .catch(() => setStatus("fail"));
      return;
    }

    // 기존 전체 신청 반송 처리
    fetch(`/api/reject/${application_id}`, { method: "POST" })
      .then(async (res) => {
        if (res.ok) return setStatus("success");
        if (res.status === 409) {
          const data = await res.json();
          if (data.error?.includes('입금대기 또는 완료 상태에서는')) {
            setStatus("cannotReject");
          } else {
            setStatus("alreadyRejected");
          }
        } else {
          setStatus("fail");
        }
      })
      .catch(() => setStatus("fail"));
  }, [application_id, location.search]);

  // 홈으로 가기
  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f7fa"
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          minWidth: 340,
          maxWidth: 420,
          textAlign: "center",
        }}
      >
        {status === "loading" && (
          <>
            <CircularProgress color="primary" size={44} sx={{ mb: 2 }} />
            <Typography variant="h6" fontWeight={700}>
              반송 요청을 처리하고 있습니다...
            </Typography>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              반송 신청이 완료되었습니다!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              택배사 영업일 기준 2~3일 안에 반송 처리 드리겠습니다.
            </Typography>
            <Typography variant="body2" color="text.disabled">
              문의가 필요하시면 고객센터로 연락해 주세요.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                size="large"
                onClick={handleGoHome}
                sx={{
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 700,
                  boxShadow: 2,
                }}
              >
                홈으로 가기
              </Button>
            </Stack>
          </>
        )}

        {status === "alreadyRejected" && (
          <>
            <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h6" fontWeight={700} color="warning.main" gutterBottom>
              이미 반송이 접수된 신청입니다
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              이 신청건은 이미 반송 처리 또는 접수된 상태입니다.<br />
              추가 문의가 필요하시면 고객센터로 연락해 주세요.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                size="large"
                onClick={handleGoHome}
                sx={{
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 700,
                  boxShadow: 2,
                }}
              >
                홈으로 가기
              </Button>
            </Stack>
          </>
        )}

        {status === "cannotReject" && (
          <>
            <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h6" fontWeight={700} color="warning.main" gutterBottom>
              입금대기 또는 완료 상태에서는 반송을 요청할 수 없습니다
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              이 신청건은 이미 입금 처리가 진행 중이거나 완료된 상태입니다.<br />
              추가 문의가 필요하시면 고객센터로 연락해 주세요.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                size="large"
                onClick={handleGoHome}
                sx={{
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 700,
                  boxShadow: 2,
                }}
              >
                홈으로 가기
              </Button>
            </Stack>
          </>
        )}

        {status === "fail" && (
          <>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 56, mb: 2 }} />
            <Typography variant="h6" fontWeight={700} color="error" gutterBottom>
              반송 처리에 실패했습니다
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              시스템 오류이거나 신청서 정보를 찾을 수 없습니다.
              <br />
              계속 문제가 발생할 경우 고객센터로 문의해 주세요.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => window.location.reload()}
                sx={{
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 700,
                }}
              >
                다시 시도
              </Button>
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                size="large"
                onClick={handleGoHome}
                sx={{
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 700,
                  boxShadow: 2,
                }}
              >
                홈으로 가기
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    </Box>
  );
}
